import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import type { BlockRenderProps } from '../../types';

const CHEF_NOTE_COUNT = 4;

function philosophyParagraphs(body: string): string[] {
  return body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function chefStoryModifier(registryBlockKey?: string): string {
  if (registryBlockKey === REGISTRY_BLOCK.presentation.chefStoryQuoteLed) {
    return 'ws-chef-story--quote-led';
  }
  if (registryBlockKey === REGISTRY_BLOCK.presentation.chefStoryKitchenStrip) {
    return 'ws-chef-story--kitchen-strip';
  }
  if (registryBlockKey === REGISTRY_BLOCK.presentation.chefStoryNotes) {
    return 'ws-chef-story--notes';
  }
  return 'ws-chef-story--portrait-split';
}

type ChefNote = {
  index: number;
  label: string;
  body: string;
  image?: string;
};

function readChefNotes(
  block: BlockRenderProps['block'],
  locale: string,
): ChefNote[] {
  const notes: ChefNote[] = [];
  for (let index = 0; index < CHEF_NOTE_COUNT; index += 1) {
    const label = pickBlockField(block.content, `noteLabel${index}`, locale);
    const body = pickBlockField(block.content, `noteBody${index}`, locale);
    const image = pickMediaUrl(block, `noteImage${index}`);
    if (!label.trim() && !body.trim()) continue;
    notes.push({ index, label, body, image });
  }
  return notes;
}

export function ChefStoryBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const chefName = pickBlockField(block.content, 'chefName', locale);
  const chefTitle = pickBlockField(block.content, 'chefTitle', locale);
  const body = pickBlockField(block.content, 'body', locale);
  const portraitImage = pickMediaUrl(block, 'portraitImage');
  const portraitSrc = portraitImage ? buildSiteImageProxyUrl(portraitImage, 720, 78) : undefined;
  const portraitSrcSet = portraitImage
    ? buildSiteImageSrcSet(portraitImage, [480, 720, 960], 78)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const modifier = chefStoryModifier(block.registryBlockKey);

  if (modifier === 'ws-chef-story--notes') {
    const notes = readChefNotes(block, locale);
    return (
      <section
        className={['ws-chef-story', modifier, isActive ? 'ws-block--active' : '']
          .filter(Boolean)
          .join(' ')}
        data-block-id={block.blockId}
        data-component={block.componentType}
        data-registry-key={block.registryBlockKey}
        id="story"
      >
        <div className="ws-chef-story__inner">
          <header className="ws-chef-story__masthead">
            <EditableContent
              fieldKey="chefTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__role"
            >
              {chefTitle}
            </EditableContent>
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__title"
            >
              {sectionTitle}
            </EditableContent>
            <EditableContent
              fieldKey="chefName"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__name"
            >
              {chefName}
            </EditableContent>
          </header>
          <div className="ws-chef-story__notes">
            {notes.map((note) => {
              const src = note.image ? buildSiteImageProxyUrl(note.image, 720, 78) : undefined;
              const srcSet = note.image
                ? buildSiteImageSrcSet(note.image, [240, 480, 720], 78)
                : undefined;
              const imageActive =
                Boolean(editable) &&
                target?.kind === 'media-field' &&
                target.fieldKey === `noteImage${note.index}`;
              return (
                <article key={note.index} className="ws-chef-story__note">
                  {src ? (
                    <figure className="ws-chef-story__note-figure">
                      <EditableMedia
                        fieldKey={`noteImage${note.index}`}
                        editable={editable}
                        activeEditTarget={target}
                        className="ws-chef-story__note-photo"
                        src={src}
                        srcSet={srcSet}
                        sizes={SITE_IMAGE_SIZES.chefNote}
                        alt={note.label || sectionTitle || 'Kitchen note'}
                        width={480}
                        height={480}
                        loading="lazy"
                        decoding="async"
                      />
                    </figure>
                  ) : editable ? (
                    <button
                      type="button"
                      className={[
                        'ws-chef-story__note-figure',
                        'ws-chef-story__note-figure--empty',
                        'ws-edit-media',
                        imageActive ? 'ws-edit-child--active ws-edit-media--active' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      data-edit-child="media-field"
                      data-field-key={`noteImage${note.index}`}
                      aria-label={note.label || 'Note photo'}
                    />
                  ) : null}
                  <div className="ws-chef-story__note-copy">
                    <EditableContent
                      fieldKey={`noteLabel${note.index}`}
                      editable={editable}
                      activeEditTarget={target}
                      className="ws-chef-story__note-label"
                    >
                      {note.label}
                    </EditableContent>
                    <EditableContent
                      as="p"
                      fieldKey={`noteBody${note.index}`}
                      editable={editable}
                      activeEditTarget={target}
                      className="ws-chef-story__note-body"
                    >
                      {note.body}
                    </EditableContent>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={['ws-chef-story', modifier, isActive ? 'ws-block--active' : '']
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="story"
    >
      <div className="ws-chef-story__inner">
        <div className="ws-chef-story__grid">
          <div className="ws-chef-story__portrait">
            {portraitImage ? (
              <EditableMedia
                fieldKey="portraitImage"
                editable={editable}
                activeEditTarget={target}
                className="ws-chef-story__portrait-frame"
                src={portraitSrc}
                srcSet={portraitSrcSet}
                sizes={SITE_IMAGE_SIZES.chefPortrait}
                alt={chefName || sectionTitle || 'Chef portrait'}
                width={480}
                height={600}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="ws-chef-story__portrait-placeholder" aria-hidden>
                {chefName ? (
                  <span className="ws-chef-story__portrait-fallback-label">{chefName}</span>
                ) : null}
              </div>
            )}
          </div>
          <div className="ws-chef-story__copy">
            <EditableContent
              as="h2"
              fieldKey="sectionTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__title"
            >
              {sectionTitle}
            </EditableContent>
            <EditableContent
              fieldKey="chefName"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__name"
            >
              {chefName}
            </EditableContent>
            <EditableContent
              fieldKey="chefTitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__role"
            >
              {chefTitle}
            </EditableContent>
            <EditableContent
              fieldKey="body"
              editable={editable}
              activeEditTarget={target}
              className="ws-chef-story__body"
            >
              {body}
            </EditableContent>
          </div>
        </div>
      </div>
    </section>
  );
}
